import { InventoryContentOption } from '@openx/types/targeting/inventoryAndContent';
import { bigNumberWithCommas } from '@openx/utils/lib/bigNumberWithCommas';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

// Backend `appGroupSearch` rejects requests with more than this many names.
export const APP_GROUP_NAME_LIMIT = 2000;

export const INVENTORY_LIMITS = {
  [InventoryContentOption.APP_BUNDLE_ID]: 20000,
  [InventoryContentOption.PAGE_URL]: 2000,
  [InventoryContentOption.KEYWORDS]: 2000,
  [InventoryContentOption.PUBLISHER_INVENTORY_RULES]: 3000,
  [InventoryContentOption.APP_GROUP]: APP_GROUP_NAME_LIMIT,
  [InventoryContentOption.LINE_ITEM_APP_BUNDLE_ID]: 20000,
};

export const bulkAddHelperPlaceholders = (limit: number, t: TFunction) => ({
  [InventoryContentOption.APP_BUNDLE_ID]: t('Enter up to {formatLimit} App Bundle IDs.', {
    formatLimit: bigNumberWithCommas(limit),
  }),
  [InventoryContentOption.PAGE_URL]: t(
    'Page URL targeting is limited to {formatLimit} entries and it is case-sensitive.',
    { formatLimit: bigNumberWithCommas(limit) }
  ),
  [InventoryContentOption.PUBLISHER_INVENTORY_RULES]: t(
    'Custom Blocking targeting is limited to {formatLimit} entries and it is case-sensitive.',
    { formatLimit: bigNumberWithCommas(limit) }
  ),
  [InventoryContentOption.APP_GROUP]: t('Enter up to {formatLimit} App Names.', {
    formatLimit: bigNumberWithCommas(limit),
  }),

  [InventoryContentOption.LINE_ITEM_APP_BUNDLE_ID]: t('Enter up to {formatLimit} App Bundle IDs.', {
    formatLimit: bigNumberWithCommas(limit),
  }),
});

export const useInventoryAndContentLabels = () => {
  const { t } = useTranslation();
  return {
    [InventoryContentOption.APP_BUNDLE_ID]: {
      label: t('App Bundle ID'),
    },
    [InventoryContentOption.PAGE_URL]: {
      label: t('Page URL'),
    },
    [InventoryContentOption.INVENTORY]: {
      label: t('Inventory'),
    },
    [InventoryContentOption.ADUNIT]: {
      label: t('Ad Unit ID'),
    },
    [InventoryContentOption.ADUNIT_SIZE]: {
      label: t('Ad Unit Size'),
    },
    [InventoryContentOption.PUBLISHER_ID]: {
      label: t('Publisher ID'),
    },
    [InventoryContentOption.INSTANCE_HASH]: {
      label: t('Instance Hash'),
    },
    [InventoryContentOption.SITE]: {
      label: t('Site ID'),
    },
    [InventoryContentOption.KEYWORDS]: {
      label: t('Keywords'),
    },
    [InventoryContentOption.LINE_ITEM_APP_BUNDLE_ID]: {
      label: t('App Bundle ID'),
    },
    [InventoryContentOption.PUBLISHER_INVENTORY_RULES]: {
      label: {
        domain: t('Publisher ID + Domains'),
        bundle_id: t('Publisher ID + App Bundle IDs'),
      },
    },
    [InventoryContentOption.APP_GROUP]: {
      label: t('App Inventory'),
    },
  };
};

export const ID_REGEX = /^[0-9]*$/;
export const INSTANCE_HASH_REGEX = /^[0-9a-f]{8}-?([0-9a-f]{4}-?){3}([0-9a-f]{12}|[0-9a-f]{6}|[0-9a-f]{20})$/;
export const INVENTORY_IDS_DISPLAY_LIMIT = 5;

export const inventoryAndContentDefaultOptions = {
  availableOptions: [
    InventoryContentOption.APP_BUNDLE_ID,
    InventoryContentOption.INVENTORY,
    InventoryContentOption.PAGE_URL,
    InventoryContentOption.ADUNIT_SIZE,
    InventoryContentOption.PUBLISHER_ID,
    InventoryContentOption.INSTANCE_HASH,
    InventoryContentOption.CONTENT_INTER_DIMENSION_OPERATOR,
    InventoryContentOption.KEYWORDS,
    InventoryContentOption.APP_GROUP,
  ],
  defaultOption: InventoryContentOption.APP_BUNDLE_ID,
};
